<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-kaia-logo="false"
    :show-lang-picker="false"
    theme="blue-background"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>

    <!-- Copy leads, illustration follows — the reverse of
         SelfTestPreSignupReflection, which opens with its image. -->
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p
          v-if="titleKey"
          class="kds-typography-title-xlarge kds-typography-center"
        >
          {{ $t(titleKey) }}
        </p>
        <p
          v-if="bodyKey"
          class="kds-typography-paragraph-large kds-typography-center"
          data-qa="reflection-body"
        >
          {{ $t(bodyKey) }}
        </p>
      </div>
      <img
        v-if="imageUrl"
        class="reflection-image"
        :src="imageUrl"
        :width="imageWidth"
        :height="imageHeight"
        alt=""
      />
    </div>

    <template #actions>
      <BaseButton
        :text="$t(ctaKey)"
        data-qa="submit"
        @click="handleNextEvent"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import ROUTE from '@shared/modules/assessment/router/names'
import useRenewalAssessmentStore from '@shared/modules/assessment/stores/useRenewalAssessmentStore'
import {
  PGIC_OVERALL_CHANGE_OPTIONS,
  GOAL_ACHIEVEMENT_OPTIONS,
  THERAPY_HELPFULNESS_OPTIONS,
} from '@shared/modules/assessment/config/renewalAssessmentOptions.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import BaseButton from '@shared/components/BaseButton.vue'

defineProps({
  progress: { type: Number, default: null },
  handleNextEvent: { type: Function, default: () => {} },
})

const emit = defineEmits(['back'])

const route = useRoute()
const renewalAssessmentStore = useRenewalAssessmentStore()

// All three reflections are the same screen reacting to a different answer, so
// they share one component the way SelfTestPreSignupReflection does — the route
// says which answer to read, the answer's option entry carries image and copy.
const selectedOption = computed(() => {
  switch (route.name) {
    case ROUTE.ASSESSMENT_RENEWAL_GOAL_ACHIEVEMENT_REFLECTION:
      return GOAL_ACHIEVEMENT_OPTIONS.find(
        (option) => option.key === renewalAssessmentStore.goalAchievement?.key,
      )
    case ROUTE.ASSESSMENT_RENEWAL_THERAPY_FEEDBACK_REFLECTION:
      return THERAPY_HELPFULNESS_OPTIONS.find(
        (option) =>
          option.key === renewalAssessmentStore.therapyHelpfulness?.key,
      )
    default:
      return PGIC_OVERALL_CHANGE_OPTIONS.find(
        (option) => option.key === renewalAssessmentStore.overallChange?.key,
      )
  }
})

// The PGIC reflection is mid-flow, so it just continues. The goal and therapy
// reflections are the last screen — their CTA is the renewal itself, and
// tapping it submits both questionnaires and leaves the flow.
const ctaKey = computed(() =>
  route.name === ROUTE.ASSESSMENT_RENEWAL_PGIC_REFLECTION
    ? 'eu__self_test_reflection__cta'
    : 'eu__renewal_assessment__reflection__extend_cta',
)

const titleKey = computed(() => selectedOption.value?.reflectionTitle ?? null)
const bodyKey = computed(() => selectedOption.value?.reflectionBody ?? null)
const imageUrl = computed(() => selectedOption.value?.image ?? null)
const imageWidth = computed(() => selectedOption.value?.imageWidth)
const imageHeight = computed(() => selectedOption.value?.imageHeight)
</script>

<style scoped lang="scss">
.reflection-image {
  display: block;
  width: 100%;
  max-width: 400px;
  height: auto;
  margin: 0 auto;
  border: 1px solid $kds-color-background-blue-accent;
  border-radius: $kds-space-1x;
}
</style>
